import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  ChatBubbleOutline as ChatIcon,
  Terminal as SystemIcon,
  Person as UserIcon,
} from "@mui/icons-material";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Prompt, TableColumn } from "../../types";
import { formatRelativeTime } from "../../utils/formatters";

const PromptList: React.FC = () => {
  const navigate = useNavigate();
  const alert = useAlert();

  // State variables
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Fetch prompts
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setLoading(true);
        const response = await api.prompts.getAll();
        setPrompts(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching prompts:", error);
        setError("Failed to load prompts. Please try again.");
        alert.error("Failed to fetch prompts");
      } finally {
        setLoading(false);
      }
    };

    fetchPrompts();
  }, [alert]);

  // Table columns
  const columns: TableColumn[] = [
    {
      field: "name",
      headerName: "Name",
      type: "link",
      linkPath: (row: Prompt) => `/prompts/${row._id}`,
    },
    {
      field: "type",
      headerName: "Type",
      type: "custom",
      render: (row: Prompt) => {
        const typeIcons = {
          system: <SystemIcon fontSize="small" />,
          user: <UserIcon fontSize="small" />,
          chat: <ChatIcon fontSize="small" />,
        };

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {typeIcons[row.type as keyof typeof typeIcons]}
            <span style={{ textTransform: "capitalize" }}>{row.type}</span>
          </Box>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      type: "string",
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      type: "custom",
      render: (row: Prompt) => formatRelativeTime(new Date(row.updatedAt)),
    },
  ];

  // Handle edit
  const handleEdit = (prompt: Prompt) => {
    navigate(`/prompts/${prompt._id}`);
  };

  // Handle delete
  const handleDelete = (prompt: Prompt) => {
    setPromptToDelete(prompt);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!promptToDelete) return;

    try {
      setIsDeleting(true);
      await api.prompts.delete(promptToDelete._id);
      setPrompts(prompts.filter((p) => p._id !== promptToDelete._id));
      alert.success("Prompt deleted successfully");
    } catch (error) {
      console.error("Error deleting prompt:", error);
      alert.error("Failed to delete prompt");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setPromptToDelete(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPromptToDelete(null);
  };

  return (
    <Box>
      <PageHeader
        title="Prompts"
        subtitle="Manage system and user prompts for your evaluations"
        actionPath="/prompts/new"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Prompts", path: "/prompts" },
        ]}
      />

      <LoadingBackdrop open={loading && !error && prompts.length === 0} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      <DataTable
        title="All Prompts"
        data={prompts}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Prompt"
        message={`Are you sure you want to delete the prompt "${promptToDelete?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={isDeleting}
      />
    </Box>
  );
};

export default PromptList;
